import { BASE_URL } from '@/services/api'
import { rememberBootColors } from '@/services/bootColors'

// Couleurs de la plateforme, réglées dans le Landlord (Paramètres → Intégrations) :
// - pages où la société n'est pas connue (connexion, inscription, mot de passe oublié,
//   connexion Landlord…) ;
// - couleurs par défaut du back-office (boutique sans personnalisation) — voir useBranding.
// Sans réglage, tout garde la couleur d'origine du thème.
const STYLE_TAG_ID = '__platform_color_style__'
const CACHE_KEY = 'platform.primary_color'
const SIDEBAR_TEXT_CACHE_KEY = 'platform.sidebar_text_color'
// classe posée sur <body> par le routeur sur ces pages uniquement
export const PLATFORM_THEMED_CLASS = 'platform-themed'

const isHex = (color) => /^#[0-9a-f]{6}$/i.test(color || '')

function channels(hex) {
    return [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16))
}

function darken(hex, amount = 15) {
    return '#' + channels(hex).map((v) => Math.max(0, v - amount).toString(16).padStart(2, '0')).join('')
}

export function applyPlatformColor(color) {
    const root = document.documentElement
    document.getElementById(STYLE_TAG_ID)?.remove()

    if (!isHex(color)) {
        root.style.removeProperty('--platform-primary')
        root.style.removeProperty('--platform-primary-rgb')
        rememberBootColors(BOOT_KEY, STYLE_TAG_ID, BOOT_VARS, '')
        return
    }

    const dark = darken(color)
    // lues par les styles des pages de connexion : var(--platform-primary, <couleur par défaut>)
    root.style.setProperty('--platform-primary', color)
    root.style.setProperty('--platform-primary-rgb', channels(color).join(', '))

    // classes du thème (boutons, liens) sur ces pages, fenêtres de confirmation comprises
    const scope = `body.${PLATFORM_THEMED_CLASS}`
    const style = document.createElement('style')
    style.id = STYLE_TAG_ID
    style.textContent = `
        ${scope} .btn-primary, ${scope} .btn-primary.disabled, ${scope} .btn-primary:disabled {
            background-color: ${color} !important;
            border-color: ${color} !important;
            color: #fff !important;
        }
        ${scope} .btn-primary:hover, ${scope} .btn-primary:focus, ${scope} .btn-primary:active {
            background-color: ${dark} !important;
            border-color: ${dark} !important;
        }
        ${scope} .link-primary, ${scope} .text-primary { color: ${color} !important; }
        ${scope} .link-primary:hover { color: ${dark} !important; }
        ${scope} .form-check-input:checked { background-color: ${color} !important; border-color: ${color} !important; }
    `
    document.head.appendChild(style)

    // réappliquées par index.html avant le premier affichage (page de connexion comprise)
    rememberBootColors(BOOT_KEY, STYLE_TAG_ID, BOOT_VARS, style.textContent)
}

const BOOT_KEY = 'backoffice.boot.platform'
const BOOT_VARS = ['--platform-primary', '--platform-primary-rgb']

/** Couleurs par défaut du back-office (dernières connues), null si non réglées. */
export function platformColors() {
    const read = (key) => {
        try {
            const value = localStorage.getItem(key)
            return isHex(value) ? value : null
        } catch (e) {
            return null
        }
    }

    return { primary_color: read(CACHE_KEY), sidebar_text_color: read(SIDEBAR_TEXT_CACHE_KEY) }
}

// Au démarrage : la dernière couleur connue tout de suite (pas de flash), puis celle de l'API
export function loadPlatformColor() {
    try {
        applyPlatformColor(localStorage.getItem(CACHE_KEY))
    } catch (e) { /* stockage indisponible */ }

    return fetch(String(BASE_URL).replace(/\/+$/, '') + '/platform/public-config', { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((config) => {
            if (!config) return
            const color = isHex(config.platform_primary_color) ? config.platform_primary_color : ''
            applyPlatformColor(color)
            const sidebarText = isHex(config.platform_sidebar_text_color) ? config.platform_sidebar_text_color : ''
            try {
                color ? localStorage.setItem(CACHE_KEY, color) : localStorage.removeItem(CACHE_KEY)
                sidebarText ? localStorage.setItem(SIDEBAR_TEXT_CACHE_KEY, sidebarText) : localStorage.removeItem(SIDEBAR_TEXT_CACHE_KEY)
            } catch (e) { /* stockage indisponible */ }
        })
        .catch(() => { /* API injoignable : couleur en cache ou par défaut */ })
}
